/**
 * Taylors Wine Finder — <quiz-flow> web component.
 *
 * Loaded only by sections/quiz.liquid (kept out of the Vite main bundle on purpose).
 * Panels are theme blocks rendered server-side; this component owns navigation,
 * scoring, outcome rendering, the LLM copy lookup, and theme-editor integration.
 *
 * On submit it pushes a Klaviyo custom event (the quiz results) and, when a list
 * id is configured, records email marketing consent — both client-side with the
 * public key, best-effort so a slow/failed call never blocks the reveal.
 *   - trackQuizEvent(name, detail)  → console.debug + DOM CustomEvent (local analytics)
 */

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

/* Weighting of the two scoring signals: explicit product-list hits vs the
   implied taste-profile affinity (see docs/figma/quiz/EXTRACTION.md). */
const HIT_WEIGHT = 0.7;
const AFFINITY_WEIGHT = 0.3;
const MAX_SCORE = 99; // never show a flat 100% — keeps 97/91-style credibility

/* Klaviyo client-side API — public key as company_id, no private token.
   https://developers.klaviyo.com/en/reference/create_client_event */
const KLAVIYO_BASE = 'https://a.klaviyo.com/client';
const KLAVIYO_REVISION = '2026-04-15';

function trackQuizEvent(name, detail = {}) {
  // STUB: wire to the site's analytics/dataLayer later.
  // eslint-disable-next-line no-console
  console.debug('[quiz] track (stub)', name, detail);
  document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

/* Normalise a typed name to capitalised form for the Klaviyo profile, so EDMs
   read "Kurtis Tozer" regardless of how it was typed ("kurtis tozer", "KURTIS").
   Lower-cases first, then upper-cases the first letter of each word (also after
   hyphens/apostrophes) — Unicode-aware, so accented names capitalise correctly. */
function titleCaseName(value) {
  return (value || '')
    .toLowerCase()
    .replace(/(^|[\s'’-])(\p{L})/gu, (_match, sep, letter) => sep + letter.toUpperCase());
}

class QuizFlow extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    const { signal } = this._abort;

    this.panels = Array.from(this.querySelectorAll('[data-quiz-panel]'));
    if (!this.panels.length) return;

    this.questions = this.panels
      .filter((panel) => panel.dataset.quizPanel === 'question')
      .map((panel, index) => this._readQuestion(panel, index));

    this.copyData = this._readJson('[data-quiz-copy]');
    this.config = this._readJson('[data-quiz-config]') || {};
    this.matchCount = parseInt(this.dataset.matchCount || '2', 10);
    this.answers = new Map(); // questionKey -> Set of answerKeys
    this.currentIndex = 0;

    this._buildProgress();
    this._bindAnswers(signal);
    this._bindNavigation(signal);
    this._bindGate(signal);

    if (this.hasAttribute('data-design-mode') && window.Shopify?.designMode) {
      this._bindEditor(signal);
      this._initEditorPanel(signal);
    }

    this.#preloadPanelImages();
    this.setAttribute('data-ready', '');
    this._show(this._indexFromHash() ?? 0, { focus: false, instant: true });
    trackQuizEvent('quiz:start', { questions: this.questions.length });

    window.addEventListener('popstate', () => {
      const index = this._indexFromHash();
      if (index !== null && index !== this.currentIndex) this._show(index, { fromHistory: true });
    }, { signal });
  }

  disconnectedCallback() {
    this._abort?.abort();
  }

  /* ---- setup ------------------------------------------------------------ */

  _readQuestion(panel, index) {
    const answers = Array.from(panel.querySelectorAll('[data-quiz-answer]')).map((el) => {
      let products = [];
      try {
        products = JSON.parse(el.querySelector('[data-quiz-answer-products]')?.textContent || '[]');
      } catch (error) {
        console.warn('[quiz] bad answer products JSON', el.dataset.quizKey, error);
      }
      let image = '';
      try {
        image = JSON.parse(el.querySelector('[data-quiz-answer-image]')?.textContent || '""');
      } catch { /* no image */ }
      return {
        el,
        key: el.dataset.quizKey,
        title: el.dataset.quizAnswerTitle || '',
        image: image || '',
        products,
      };
    });

    return {
      panel,
      index,
      key: panel.dataset.quizKey,
      title: panel.dataset.quizTitle || panel.dataset.quizKey,
      mode: panel.dataset.quizMode === 'multi' ? 'multi' : 'single',
      weight: Math.max(0, parseInt(panel.dataset.quizWeight || '50', 10)),
      answers,
    };
  }

  _readJson(selector) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || 'null');
    } catch {
      return null;
    }
  }

  _buildProgress() {
    const total = this.questions.length;
    this.questions.forEach((question, qIndex) => {
      const list = question.panel.querySelector('[data-quiz-progress]');
      if (!list) return;
      list.innerHTML = '';
      for (let i = 0; i < total; i += 1) {
        const li = document.createElement('li');
        li.textContent = `${ROMAN[i] || i + 1} .`;
        if (i === qIndex) li.classList.add('is-current');
        list.append(li);
      }
    });
  }

  /* ---- navigation --------------------------------------------------------- */

  _bindNavigation(signal) {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-quiz-back]')) this._show(this.currentIndex - 1);
      if (event.target.closest('[data-quiz-next]')) this._advanceFrom(this.currentIndex);
      if (event.target.closest('[data-quiz-restart]')) this._restart();
      if (event.target.closest('.quiz-start')) this._show(this.currentIndex + 1);
    }, { signal });
  }

  _show(index, { focus = true, fromHistory = false, instant = false } = {}) {
    const clamped = Math.min(Math.max(index, 0), this.panels.length - 1);
    const panel = this.panels[clamped];
    if (!panel) return;

    // Leaving a loading panel cancels any pending auto-advance/animation.
    this._cancelLoading();

    // Mask card-fetch latency behind the email gate.
    if (panel.dataset.quizPanel === 'submission') {
      this._prefetchMatches();
      this._resetGate(panel); // arrive at the gate with a clickable button, however we got here
    }
    // Build the outcome behind the loading screen so the reveal is instant.
    if (panel.dataset.quizPanel === 'loading') this._renderOutcome();
    if (panel.dataset.quizPanel === 'outcome') this._renderOutcome();

    this.#preloadPanelImages(panel);
    if (this.panels[clamped + 1]) this.#preloadPanelImages(this.panels[clamped + 1]);

    if (instant) this.classList.add('quiz__flow--instant');
    this.panels.forEach((p) => p.classList.toggle('is-active', p === panel));
    this.currentIndex = clamped;
    if (instant) {
      requestAnimationFrame(() => this.classList.remove('quiz__flow--instant'));
    }

    if (panel.dataset.quizPanel === 'loading') this._playLoading(panel, clamped);

    if (!fromHistory && !instant) {
      history.pushState(null, '', `#quiz-${clamped}`);
    }

    if (focus && !instant) {
      panel.querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
    }

    this.#scrollQuizIntoView({ instant, fromHistory });
  }

  #preloadPanelImages(...panels) {
    const targets = panels.length ? panels : this.panels;
    targets.forEach((panel) => {
      panel.querySelectorAll('.quiz-panel__image, .quiz-answer__image').forEach((img) => {
        if (img.complete) return;
        const src = img.currentSrc || img.src;
        if (!src) return;
        const preload = new Image();
        preload.src = src;
      });
    });
  }

  #scrollQuizIntoView({ instant = false, fromHistory = false } = {}) {
    if (instant) return;
    const section = this.closest('.shopify-section');
    if (!section) return;
    const offScreen = section.getBoundingClientRect().top < 0;
    if (fromHistory && !offScreen) return;
    if (!offScreen) return;
    section.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  /* ---- loading panel ------------------------------------------------------- */

  _playLoading(panel, index) {
    const stage = panel.querySelector('.quiz-loading');
    if (!stage) return;
    const duration = Math.max(300, parseInt(panel.dataset.quizDuration || '1500', 10));
    const counters = panel.querySelectorAll('[data-quiz-pct]');
    const setFill = (fill) => {
      stage.style.setProperty('--quiz-fill', String(fill));
      if (panel.hasAttribute('data-quiz-show-pct')) {
        const pct = `${Math.round(fill * 100)}%`;
        counters.forEach((el) => { el.textContent = pct; });
      }
    };

    // In the theme editor, hold a representative frame so merchants can style
    // it — don't auto-advance away from the selected block.
    if (this.hasAttribute('data-design-mode')) {
      setFill(0.6);
      return;
    }

    trackQuizEvent('quiz:loading', { duration });

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const advance = () => {
      // Guard like the source app: only reveal if still on this loading panel
      // (a restart or back gesture cancels it).
      if (this.currentIndex === index) this._advanceFrom(index);
    };

    if (reduceMotion) {
      setFill(0.6);
      this._loadingTimer = window.setTimeout(advance, duration);
      return;
    }

    const start = this._now();
    const tick = (nowArg) => {
      const now = nowArg ?? this._now();
      const elapsed = now - start;
      const fill = Math.min(1, elapsed / duration);
      setFill(fill);
      if (elapsed < duration) {
        this._loadingRaf = window.requestAnimationFrame(tick);
      } else {
        advance();
      }
    };
    this._loadingRaf = window.requestAnimationFrame(tick);
  }

  _cancelLoading() {
    if (this._loadingRaf) window.cancelAnimationFrame(this._loadingRaf);
    if (this._loadingTimer) window.clearTimeout(this._loadingTimer);
    this._loadingRaf = null;
    this._loadingTimer = null;
  }

  _now() {
    return window.performance?.now ? window.performance.now() : Date.now();
  }

  _indexFromHash() {
    const match = window.location.hash.match(/^#quiz-(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  }

  _advanceFrom(index) {
    this._show(index + 1);
  }

  _restart() {
    this.answers.clear();
    this.querySelectorAll('.quiz-answer__input').forEach((input) => { input.checked = false; });
    this.questions.forEach((question) => {
      const next = question.panel.querySelector('[data-quiz-next]');
      if (next) next.hidden = true;
    });
    this.panels.filter((panel) => panel.dataset.quizPanel === 'submission').forEach((panel) => this._resetGate(panel));
    trackQuizEvent('quiz:restart');
    this._show(0);
  }

  /* Restore a gate panel to its ready state: clear the submit button's busy
     visual and hide any stale error. Deliberately keeps the name/email values
     (autofill / remembered email is wanted). */
  _resetGate(panel) {
    panel.querySelector('[data-quiz-gate] [type="submit"]')?.removeAttribute('aria-busy');
    const error = panel.querySelector('[data-quiz-gate-error]');
    if (error) error.hidden = true;
  }

  /* ---- answers ------------------------------------------------------------- */

  _bindAnswers(signal) {
    this.questions.forEach((question) => {
      question.answers.forEach((answer) => {
        const input = answer.el.querySelector('.quiz-answer__input');
        input?.addEventListener('change', () => {
          if (question.mode === 'single') {
            question.answers.forEach((other) => {
              if (other !== answer) other.el.querySelector('.quiz-answer__input').checked = false;
            });
            this._setAnswer(question, [answer.key]);
            trackQuizEvent('quiz:answer', { question: question.key, answers: [answer.key] });
            const panelIndex = this.panels.indexOf(question.panel);
            window.setTimeout(() => this._advanceFrom(panelIndex), 220);
          } else {
            const selected = question.answers
              .filter((a) => a.el.querySelector('.quiz-answer__input').checked)
              .map((a) => a.key);
            this._setAnswer(question, selected);
            const next = question.panel.querySelector('[data-quiz-next]');
            if (next) next.hidden = selected.length === 0;
            trackQuizEvent('quiz:answer', { question: question.key, answers: selected });
          }
        }, { signal });
      });
    });
  }

  _setAnswer(question, keys) {
    this.answers.set(question.key, new Set(keys));
  }

  _selectedAnswers(question) {
    const keys = this.answers.get(question.key) || new Set();
    return question.answers.filter((answer) => keys.has(answer.key));
  }

  /* ---- gate ------------------------------------------------------------------ */

  _bindGate(signal) {
    this.querySelectorAll('[data-quiz-gate]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const error = form.querySelector('[data-quiz-gate-error]');
        const name = form.querySelector('input[name="quiz-name"]')?.value.trim() || '';
        const email = form.querySelector('input[name="quiz-email"]')?.value.trim() || '';
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!name || !emailOk) {
          if (error) {
            error.textContent = !name
              ? 'Please tell us your name.'
              : 'That email doesn’t look right — mind checking it?';
            error.hidden = false;
          }
          return;
        }

        if (error) error.hidden = true;

        // No aria-busy spinner on the submit button here. The theme's
        // <button-content> loader toggles via an async MutationObserver that
        // early-returns on a hidden element, so clearing it during the advance
        // (which hides this panel in the same tick) orphaned the spinner and
        // left the button looking permanently busy on return. The loading
        // panel — or the instant advance when there is none — is the feedback.

        // Best-effort: the Klaviyo push must never block or fail the reveal.
        this._submitLead({ name, email });
        // Submitting the email opts the visitor into member pricing — reveal it
        // on the outcome cards (and the rest of the site) if the hook exists.
        if (typeof window.enableMemberPricing === 'function') window.enableMemberPricing();
        trackQuizEvent('quiz:submit', { email_domain: email.split('@')[1] });
        this._advanceFrom(this.currentIndex);
      }, { signal });
    });
  }

  _answersPayload() {
    return this.questions.map((question) => ({
      question: question.key,
      answers: Array.from(this.answers.get(question.key) || []),
    }));
  }

  /* ---- klaviyo ------------------------------------------------------------------ */

  /* handle → Klaviyo product object, from every answer's products plus the
     outcome block's fallback detail. Built once, lazily. */
  _productDetail() {
    if (this._productDetailMap) return this._productDetailMap;
    const map = new Map();
    this.questions.forEach((question) => {
      question.answers.forEach((answer) => {
        answer.products.forEach((product) => {
          if (product.klaviyo && !map.has(product.handle)) map.set(product.handle, product.klaviyo);
        });
      });
    });
    (this._readJson('[data-quiz-fallback-detail]') || []).forEach((obj) => {
      if (obj && obj.Handle && !map.has(obj.Handle)) map.set(obj.Handle, obj);
    });
    this._productDetailMap = map;
    return map;
  }

  /* The event properties merchants build flows on and populate EDMs with. Keys
     are handleized; `matches` are full Klaviyo product objects (with a Score),
     and `answers_detail` carries each chosen answer's title and image. */
  _buildKlaviyoProperties() {
    const klaviyo = this.config.klaviyo || {};
    const detail = this._productDetail();
    const answers = {};
    const answersDetail = [];
    const selected = new Set();

    this.questions.forEach((question) => {
      const keys = Array.from(this.answers.get(question.key) || []);
      answers[question.key] = keys;
      this._selectedAnswers(question).forEach((answer) => {
        answer.products.forEach((product) => selected.add(product.handle));
        answersDetail.push({
          question: question.key,
          question_title: question.title,
          answer: answer.key,
          answer_title: answer.title,
          image: answer.image || null,
        });
      });
    });

    const { scored, poolSize, answeredCount } = this._score();
    const displayMatches = this.#selectDisplayMatches(scored);
    const matches = displayMatches.map((entry) => ({
      ...(detail.get(entry.handle) || { Handle: entry.handle }),
      Score: entry.pct,
    }));

    return {
      quiz_id: klaviyo.quizId || '',
      answers,
      answers_detail: answersDetail,
      selected_products: Array.from(selected),
      matches,
      top_match: matches[0] ? (matches[0].Handle || null) : null,
      top_match_name: matches[0] ? (matches[0].Name || null) : null,
      answered_count: answeredCount,
      pool_size: poolSize,
    };
  }

  /* Fire-and-forget: a custom event (always) plus a marketing-consent
     subscription (only when a list id is configured). Never blocks the reveal. */
  _submitLead({ name, email }) {
    const klaviyo = this.config.klaviyo || {};
    if (!klaviyo.enabled || !klaviyo.publicKey) return;

    const [firstName, ...rest] = titleCaseName(name).split(/\s+/).filter(Boolean);
    const lastName = rest.join(' ');
    const profileAttrs = { email };
    if (firstName) profileAttrs.first_name = firstName;
    if (lastName) profileAttrs.last_name = lastName;

    const headers = { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION };
    const post = (path, body) => fetch(
      `${KLAVIYO_BASE}/${path}?company_id=${encodeURIComponent(klaviyo.publicKey)}`,
      { method: 'POST', headers, body: JSON.stringify(body), keepalive: true, mode: 'cors' },
    ).catch((err) => console.warn(`[quiz] klaviyo ${path} failed`, err));

    const uniqueId = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${klaviyo.quizId || 'quiz'}-${email}-${this._now()}`;

    post('events', {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: klaviyo.metric || 'Wine Finder Completed' } } },
          profile: { data: { type: 'profile', attributes: profileAttrs } },
          properties: this._buildKlaviyoProperties(),
          unique_id: uniqueId,
        },
      },
    });

    if (klaviyo.listId) {
      post('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Wine Finder Quiz',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  ...profileAttrs,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: klaviyo.listId } } },
        },
      });
    }
  }

  /* ---- scoring ------------------------------------------------------------------ */

  _score() {
    const products = new Map(); // handle -> { handle, profile, firstSeen, available }
    let order = 0;

    this.questions.forEach((question) => {
      question.answers.forEach((answer) => {
        answer.products.forEach((product) => {
          if (!products.has(product.handle)) {
            products.set(product.handle, {
              handle: product.handle,
              profile: product.profile || {},
              firstSeen: (order += 1),
              available: product.available !== false,
            });
          }
        });
      });
    });

    const answered = this.questions.filter((question) => this._selectedAnswers(question).length > 0);
    const totalWeight = answered.reduce((sum, question) => sum + question.weight, 0) || 1;

    // Implied taste vector: weighted mean of the profiles behind every selected answer.
    const traitSums = {};
    const traitWeights = {};
    answered.forEach((question) => {
      this._selectedAnswers(question).forEach((answer) => {
        answer.products.forEach((product) => {
          Object.entries(product.profile || {}).forEach(([trait, value]) => {
            if (!Number.isFinite(value)) return;
            traitSums[trait] = (traitSums[trait] || 0) + value * question.weight;
            traitWeights[trait] = (traitWeights[trait] || 0) + question.weight;
          });
        });
      });
    });
    const implied = {};
    Object.keys(traitSums).forEach((trait) => { implied[trait] = traitSums[trait] / traitWeights[trait]; });

    const affinityOf = (profile) => {
      const traits = Object.keys(implied).filter((trait) => Number.isFinite(profile?.[trait]));
      if (!traits.length) return 0.5;
      const meanDiff = traits.reduce((sum, trait) => sum + Math.abs(profile[trait] - implied[trait]), 0) / traits.length;
      return Math.max(0, 1 - meanDiff / 6);
    };

    const scored = [];
    products.forEach((product) => {
      let earned = 0;
      answered.forEach((question) => {
        const selected = this._selectedAnswers(question);
        const hits = selected.filter((answer) => answer.products.some((p) => p.handle === product.handle)).length;
        if (hits > 0) earned += question.weight * (hits / selected.length);
      });
      if (earned <= 0) return;

      const hit = earned / totalWeight;
      const affinity = affinityOf(product.profile);
      const score = HIT_WEIGHT * hit + AFFINITY_WEIGHT * affinity;
      scored.push({
        handle: product.handle,
        affinity,
        firstSeen: product.firstSeen,
        score,
        available: product.available,
      });
    });

    scored.sort((a, b) => b.score - a.score || b.affinity - a.affinity || a.firstSeen - b.firstSeen);

    // Displayed match % is anchored presentation, not the raw normalized score:
    // the best match lands in the high 80s–99 (scaled by its own strength), and
    // runners-up fall away proportionally to their raw-score gap — producing the
    // credible 97/91-style spread the design shows. Raw scores drive ranking.
    if (scored.length) {
      const top = scored[0].score;
      const topPct = Math.min(MAX_SCORE, 87 + Math.round(top * 12));
      scored.forEach((entry, index) => {
        entry.pct = index === 0 ? topPct : Math.max(55, topPct - Math.round((top - entry.score) * 60));
      });
    }

    // Top up from the outcome block's fallback list. Their band sits just below
    // any organic match — presented, not computed, since nothing was hit.
    if (scored.length < this.matchCount) {
      const fallbackProducts = this._readJson('[data-quiz-fallback-products]') || [];
      const floor = scored.length ? Math.min(78, scored[scored.length - 1].pct - 4) : 78;
      fallbackProducts.forEach((fallback, index) => {
        if (scored.length >= this.matchCount) return;
        const handle = typeof fallback === 'string' ? fallback : fallback.handle;
        const available = typeof fallback === 'string' ? true : fallback.available;
        if (scored.some((entry) => entry.handle === handle)) return;
        if (!this.querySelector(`template[data-quiz-product="${CSS.escape(handle)}"]`)) return;
        scored.push({
          handle,
          affinity: 0.5,
          firstSeen: 999,
          pct: Math.max(60, floor - index * 3),
          score: 0,
          available: available !== false,
        });
      });
    }

    return { scored, poolSize: products.size, answeredCount: answered.length };
  }

  #isAvailable(entry) {
    return entry.available !== false;
  }

  #normalizeMatchPercentages(matches) {
    if (!matches.length) return;
    const top = matches[0].score;
    const topPct = Math.min(MAX_SCORE, 87 + Math.round(top * 12));
    matches.forEach((entry, index) => {
      entry.pct = index === 0 ? topPct : Math.max(55, topPct - Math.round((top - entry.score) * 60));
    });
  }

  /* Tiered selection: in-stock organic → in-stock fallbacks → OOS last resort. */
  #selectDisplayMatches(scored) {
    const organic = scored.filter((entry) => entry.score > 0);
    const fallbacksInScored = scored.filter((entry) => entry.score === 0);
    const selected = [];
    const seen = new Set();

    const add = (entry) => {
      if (seen.has(entry.handle)) return;
      seen.add(entry.handle);
      selected.push({ ...entry });
    };

    for (const entry of organic) {
      if (selected.length >= this.matchCount) break;
      if (!this.#isAvailable(entry)) continue;
      add(entry);
    }

    if (selected.length < this.matchCount) {
      const fallbackProducts = this._readJson('[data-quiz-fallback-products]') || [];
      for (const fallback of fallbackProducts) {
        if (selected.length >= this.matchCount) break;
        const handle = typeof fallback === 'string' ? fallback : fallback.handle;
        const available = typeof fallback === 'string' ? true : fallback.available;
        if (available === false || seen.has(handle)) continue;
        if (!this.querySelector(`template[data-quiz-product="${CSS.escape(handle)}"]`)) continue;
        const existing = fallbacksInScored.find((entry) => entry.handle === handle);
        add(existing || { handle, affinity: 0.5, firstSeen: 999, score: 0, available: true });
      }
    }

    if (selected.length < this.matchCount) {
      for (const entry of scored) {
        if (selected.length >= this.matchCount) break;
        add(entry);
      }
    }

    this.#normalizeMatchPercentages(selected);
    return selected;
  }

  /* ---- outcome ------------------------------------------------------------------- */

  /* Matched cards are fetched from sections/quiz-product-card.liquid via the
     Section Rendering API (a block cannot pre-render sibling blocks' products).
     Fallback products from the outcome block are available locally. */
  _getProductCard(handle) {
    this._cardCache = this._cardCache || new Map();
    if (!this._cardCache.has(handle)) {
      const local = this.querySelector(`template[data-quiz-product="${CSS.escape(handle)}"]`);
      this._cardCache.set(handle, local
        ? Promise.resolve(local)
        : fetch(`/products/${encodeURIComponent(handle)}?section_id=quiz-product-card`)
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`)))
          )
          .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector('template[data-quiz-product]'))
          .catch((error) => {
            console.warn('[quiz] product card fetch failed', handle, error);
            return null;
          }));
    }
    return this._cardCache.get(handle);
  }

  _prefetchMatches() {
    const { scored } = this._score();
    const selected = this.#selectDisplayMatches(scored);
    const prefetch = [...selected];
    const seen = new Set(selected.map((match) => match.handle));

    for (const entry of scored) {
      if (prefetch.length >= this.matchCount + 1) break;
      if (seen.has(entry.handle) || !this.#isAvailable(entry)) continue;
      prefetch.push(entry);
      seen.add(entry.handle);
    }

    prefetch.forEach((match) => this._getProductCard(match.handle));
  }

  async _renderOutcome() {
    const outcome = this.panels.find((panel) => panel.dataset.quizPanel === 'outcome');
    const mount = outcome?.querySelector('[data-quiz-matches]');
    if (!outcome || !mount) return;

    const { scored, poolSize, answeredCount } = this._score();
    const candidates = this.#selectDisplayMatches(scored);

    // Resolve cards for the selected candidates, skipping any whose card can't render.
    const matches = [];
    for (const candidate of candidates) {
      const template = await this._getProductCard(candidate.handle);
      if (template) matches.push({ ...candidate, template });
    }

    this._lastMatches = matches.map((match) => ({ handle: match.handle, pct: match.pct }));

    const rankLabels = (outcome.dataset.quizRankLabels || '').split('|');
    const suffix = outcome.dataset.quizMatchSuffix || '% Match';

    outcome.style.setProperty('--quiz-match-columns', String(Math.min(Math.max(matches.length, 1), 2)));
    mount.innerHTML = '';

    matches.forEach((match, index) => {
      const column = document.createElement('div');
      column.className = 'quiz-match';

      const header = document.createElement('header');
      header.className = 'quiz-match__header';
      const rank = document.createElement('span');
      rank.className = 'quiz-match__rank';
      rank.textContent = rankLabels[index] || `Match ${index + 1}`;
      const score = document.createElement('span');
      score.className = 'quiz-match__score';
      score.textContent = `${match.pct}${suffix}`;
      header.append(rank, score);
      column.append(header);

      column.append(match.template.content.cloneNode(true));
      mount.append(column);
    });

    this._renderCopy(outcome);

    const meta = outcome.querySelector('[data-quiz-meta]');
    if (meta && this.hasAttribute('data-show-meta') && this.config.metaLine) {
      meta.textContent = this.config.metaLine
        .replace('[count]', String(poolSize))
        .replace('[answers]', String(answeredCount));
      meta.hidden = false;
    }

    mount.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', () => trackQuizEvent('quiz:add_to_cart', { form: form.id }), { signal: this._abort.signal });
    });

    trackQuizEvent('quiz:outcome', { matches: this._lastMatches, pool: poolSize });
  }

  /* Copy lookup: exact permutation key → wildcard tails → "*" → section fallback. */
  _renderCopy(outcome) {
    const target = outcome.querySelector('[data-quiz-copy-target]');
    if (!target) return;

    let text = this.config.fallbackCopy || '';
    const data = this.copyData;

    if (data && data.copy && Array.isArray(data.keys)) {
      const segments = data.keys.map((questionKey) => this._dominantAnswerKey(questionKey)).filter(Boolean);
      if (segments.length === data.keys.length) {
        const candidates = [];
        for (let keep = segments.length; keep >= 1; keep -= 1) {
          const head = segments.slice(0, keep).join('|');
          candidates.push(keep === segments.length ? head : `${head}|*`);
        }
        candidates.push('*');
        for (const candidate of candidates) {
          if (typeof data.copy[candidate] === 'string' && data.copy[candidate].trim()) {
            text = data.copy[candidate];
            break;
          }
        }
      }
    }

    target.textContent = text;
  }

  /* For multi-select questions the permutation key uses the dominant answer:
     the selected answer that contains the top-scoring product, else the first
     selected in DOM order. */
  _dominantAnswerKey(questionKey) {
    const question = this.questions.find((q) => q.key === questionKey);
    if (!question) return null;
    const selected = this._selectedAnswers(question);
    if (!selected.length) return null;
    if (selected.length === 1) return selected[0].key;

    const top = this._lastMatches?.[0]?.handle;
    if (top) {
      const winner = selected.find((answer) => answer.products.some((p) => p.handle === top));
      if (winner) return winner.key;
    }
    return selected[0].key;
  }

  /* ---- theme editor integration --------------------------------------------------- */

  _bindEditor(signal) {
    document.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest?.('[data-quiz-panel]');
      if (!panel || !this.contains(panel)) return;
      const index = this.panels.indexOf(panel);
      if (index !== -1) this._show(index, { focus: false, instant: event.detail?.load === true });

      const answer = event.target.closest?.('[data-quiz-answer]');
      if (answer) answer.classList.add('is-editor-selected');
    }, { signal });

    document.addEventListener('shopify:block:deselect', (event) => {
      const answer = event.target.closest?.('[data-quiz-answer]');
      if (answer && this.contains(answer)) answer.classList.remove('is-editor-selected');
    }, { signal });
  }

  /* ---- design-mode prompt builder + validator ---------------------------------------- */

  _initEditorPanel(signal) {
    const panel = this.closest('.shopify-section')?.querySelector('[data-quiz-editor-panel]');
    if (!panel) return;

    const prompt = panel.querySelector('[data-quiz-prompt]');
    if (prompt) {
      // Liquid cannot enumerate the blocks (section.blocks is opaque under
      // content_for 'blocks'), so the questions/keys/permutations are filled
      // here from the rendered DOM.
      const questionsText = this.questions.map((question, index) => {
        const answers = question.answers.map((answer) => {
          const title = answer.el.querySelector('.quiz-answer__title')?.textContent.trim() || answer.key;
          const subtitle = answer.el.querySelector('.quiz-answer__subtitle')?.textContent.trim();
          const wines = answer.products.map((product) => product.title || product.handle).filter(Boolean);
          return `  - key "${answer.key}": ${title}${subtitle ? ` — ${subtitle}` : ''}${wines.length ? ` (wines: ${wines.join(', ')})` : ''}`;
        }).join('\n');
        return `Question ${index + 1} — key "${question.key}": ${question.title} (${question.mode} answer)\n${answers}`;
      }).join('\n');

      const keysJson = JSON.stringify(this.questions.map((question) => question.key));

      const permutations = this._enumeratePermutations();
      const list = permutations.limited
        ? `${permutations.keys.join('\n')}\n… plus ${permutations.total - permutations.keys.length} more — continue the same cartesian pattern to cover all ${permutations.total}.`
        : permutations.keys.join('\n');

      prompt.value = prompt.value
        .replace('__QUIZ_QUESTIONS__', questionsText)
        .replace('__QUIZ_KEYS__', keysJson)
        .replace('__QUIZ_PERMUTATIONS__', list)
        .replace(/\n\(If any __QUIZ_\*__ token[^)]*\)\n?/, '\n');
    }

    panel.querySelector('[data-quiz-copy-prompt]')?.addEventListener('click', async () => {
      const status = panel.querySelector('[data-quiz-copy-prompt-status]');
      try {
        await navigator.clipboard.writeText(prompt.value);
        if (status) status.textContent = 'Copied ✓';
      } catch {
        prompt.select();
        if (status) status.textContent = 'Press ⌘C to copy';
      }
      window.setTimeout(() => { if (status) status.textContent = ''; }, 2500);
    }, { signal });

    this._renderValidator(panel);
    this._renderKlaviyoDoc(panel);
  }

  /* Design-mode reference: shows the exact event a Klaviyo flow can trigger on,
     with the live handleized keys and a populated example built from the first
     answer of each configured question. */
  _renderKlaviyoDoc(panel) {
    const mount = panel.querySelector('[data-quiz-klaviyo-doc]');
    if (!mount) return;
    const klaviyo = this.config.klaviyo || {};

    if (!klaviyo.enabled) {
      mount.innerHTML = '<p class="is-warn">Klaviyo sending is turned off for this section.</p>';
      return;
    }

    const rows = [];
    const add = (state, text) => rows.push(`<li class="is-${state}">${text}</li>`);
    add(klaviyo.publicKey ? 'ok' : 'fail', klaviyo.publicKey
      ? `Event <strong>${klaviyo.metric || 'Wine Finder Completed'}</strong> → account <code>${klaviyo.publicKey}</code>.`
      : 'No public API key set — nothing will be sent.');
    add(klaviyo.listId ? 'ok' : 'warn', klaviyo.listId
      ? `Submitters subscribed to list <code>${klaviyo.listId}</code> with email marketing consent.`
      : 'No list ID — the event fires but no marketing consent is recorded. Set a list ID to subscribe submitters.');

    const propDocs = [
      ['quiz_id', 'this section’s Quiz ID (handleized) — filter flows per quiz'],
      ['answers', 'object: each question handle → array of chosen answer handles'],
      ['answers_detail', 'array of chosen answers with title + image (for EDMs)'],
      ['selected_products', 'array of product handles behind the chosen answers'],
      ['matches', 'array of full product objects (Name, Brand, Price, ImageURL, URL, Categories…) + Score'],
      ['top_match', 'handle of the #1 recommended wine'],
      ['top_match_name', 'name of the #1 recommended wine (for subject lines)'],
      ['answered_count', 'number of questions answered'],
      ['pool_size', 'number of candidate wines considered'],
    ];

    // Build the example from the REAL payload builder, simulating the first
    // answer of each question so the doc always matches what actually sends.
    const savedAnswers = this.answers;
    this.answers = new Map();
    this.questions.forEach((question) => {
      const first = question.answers[0];
      if (first) this.answers.set(question.key, new Set([first.key]));
    });
    this._productDetailMap = null;
    const example = {
      metric: klaviyo.metric || 'Wine Finder Completed',
      email: 'customer@example.com',
      properties: this._buildKlaviyoProperties(),
    };
    this.answers = savedAnswers;
    this._productDetailMap = null;

    mount.innerHTML = `
      <ul>${rows.join('')}</ul>
      <p class="quiz-editor-panel__doc-note">Event properties:</p>
      <ul class="quiz-editor-panel__doc-keys">${propDocs.map(([k, d]) => `<li><code>${k}</code> — ${d}</li>`).join('')}</ul>
      <p class="quiz-editor-panel__doc-note">Example:</p>
      <pre class="quiz-editor-panel__example">${JSON.stringify(example, null, 2).replace(/</g, '&lt;')}</pre>
    `;
  }

  _enumeratePermutations(limit = 300) {
    const keySets = this.questions.map((question) => question.answers.map((answer) => answer.key));
    let combos = [''];
    for (const keys of keySets) {
      const next = [];
      for (const combo of combos) {
        for (const key of keys) next.push(combo ? `${combo}|${key}` : key);
      }
      combos = next;
    }
    const total = combos.length;
    return { keys: combos.slice(0, limit), total, limited: total > limit };
  }

  _renderValidator(panel) {
    const mount = panel.querySelector('[data-quiz-validator]');
    if (!mount) return;

    const rows = [];
    const add = (state, text) => rows.push(`<li class="is-${state}">${text}</li>`);
    const data = this.copyData;

    if (!data) {
      add('warn', 'No copy set mapped (or empty JSON) — visitors will always see the section’s default outcome copy.');
    } else if (!data.copy || !Array.isArray(data.keys)) {
      add('fail', 'Copy JSON is malformed: expected { "version", "keys": [...], "copy": {...} }.');
    } else {
      const questionKeys = this.questions.map((question) => question.key);
      const sameKeys = JSON.stringify(data.keys) === JSON.stringify(questionKeys);
      add(sameKeys ? 'ok' : 'fail', sameKeys
        ? `Question keys match (${questionKeys.join(' | ')}).`
        : `Question keys are stale: JSON has [${data.keys.join(', ')}], quiz is [${questionKeys.join(', ')}] — regenerate the prompt.`);

      const { keys: expected, total } = this._enumeratePermutations(10000);
      const missing = expected.filter((key) => {
        if (typeof data.copy[key] === 'string') return false;
        const parts = key.split('|');
        for (let keep = parts.length - 1; keep >= 1; keep -= 1) {
          if (typeof data.copy[`${parts.slice(0, keep).join('|')}|*`] === 'string') return false;
        }
        return typeof data.copy['*'] !== 'string';
      });
      add(missing.length ? 'warn' : 'ok', missing.length
        ? `${missing.length} of ${total} combinations resolve to nothing: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`
        : `All ${total} answer combinations resolve to copy.`);

      add(typeof data.copy['*'] === 'string' ? 'ok' : 'warn', typeof data.copy['*'] === 'string'
        ? 'Generic "*" fallback present.'
        : 'No "*" fallback entry — unresolved combinations fall back to the section default.');

      const valid = new Set(expected);
      const orphans = Object.keys(data.copy).filter((key) => key !== '*' && !key.endsWith('|*') && !valid.has(key));
      if (orphans.length) add('warn', `${orphans.length} entries no longer match any combination: ${orphans.slice(0, 5).join(', ')}${orphans.length > 5 ? '…' : ''}`);
    }

    add(this.config.fallbackCopy ? 'ok' : 'warn', this.config.fallbackCopy
      ? 'Section default outcome copy is set.'
      : 'Section default outcome copy is empty.');

    mount.innerHTML = `<ul>${rows.join('')}</ul>`;
  }
}

if (!customElements.get('quiz-flow')) {
  customElements.define('quiz-flow', QuizFlow);
}
